import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MDBBtn,
  MDBCol,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
} from "mdbreact";
import { SearchClinicMenus } from "../../../../components/searchables";
import { computeCP, currency } from "../../../../services/utilities";
import {
  AddToCart,
  ChangeQty,
  RemoveFromCart,
  SetItemDispenses,
} from "../../../../services/redux/slices/diagnostics/clinic/settlements";
import {
  buildBillingStockRows,
  getMedicineDosage,
  getSelectedClinic,
} from "./utils";

export default function CartPanel() {
  const dispatch = useDispatch();
  const [activeDispenseIndex, setActiveDispenseIndex] = useState(null);
  const [modalStockRows, setModalStockRows] = useState([]);
  const { selectedAppointment = null } = useSelector(
    ({ clinicalBilling }) => clinicalBilling,
  );
  const { cart = [], privilege = 0 } = useSelector(
    ({ settlements }) => settlements,
  );
  const { collections: inventoryCollections = [] } = useSelector(
    ({ clinicInventory }) => clinicInventory,
  );

  const clinic = getSelectedClinic(selectedAppointment);
  const stockRows = buildBillingStockRows({
    cart,
    inventories: inventoryCollections,
    clinicId: selectedAppointment?.clinicId || clinic?._id,
  });
  const activeCartItem =
    activeDispenseIndex === null ? null : cart[activeDispenseIndex] || null;
  const activeStockRows = useMemo(
    () =>
      activeDispenseIndex === null
        ? []
        : stockRows.filter(
            (row) => Number(row.cartIndex) === Number(activeDispenseIndex),
          ),
    [activeDispenseIndex, stockRows],
  );

  useEffect(() => {
    if (activeDispenseIndex === null) {
      setModalStockRows([]);
      return;
    }

    setModalStockRows(
      activeStockRows.map((row) => ({
        ...row,
        dispense: Number(row?.dispense || 0),
      })),
    );
    // copy the rows only when a dispense opens, so edits are not reset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDispenseIndex]);

  const handleDispenseChange = (inventoryId, value) => {
    setModalStockRows((prev) =>
      prev.map((row) => {
        if (row.inventoryId !== inventoryId) return row;

        const nextValue = Math.max(
          0,
          Math.min(Number(value || 0), Number(row?.stock || 0)),
        );

        return {
          ...row,
          dispense: nextValue,
          remaining: Math.max(Number(row?.stock || 0) - nextValue, 0),
        };
      }),
    );
  };

  const handleMenuSelect = (item = {}) => {
    const existingIndex = cart.findIndex(
      ({ _id }) => String(_id || "") === String(item?._id || ""),
    );

    dispatch(AddToCart(item));

    if (String(item?.itemType || "").toLowerCase() === "meds") {
      setActiveDispenseIndex(existingIndex > -1 ? existingIndex : cart.length);
    }
  };

  const handleDispenseSave = () => {
    if (activeDispenseIndex === null) return setActiveDispenseIndex(null);

    const nextDispenses = modalStockRows
      .map((row) => ({
        inventoryId: row.inventoryId,
        qty: Number(row?.dispense || 0),
      }))
      .filter((row) => row.qty > 0);
    const nextQty = nextDispenses.reduce(
      (sum, row) => sum + Number(row?.qty || 0),
      0,
    );

    dispatch(
      SetItemDispenses({
        index: activeDispenseIndex,
        dispenses: nextDispenses,
      }),
    );
    dispatch(ChangeQty({ index: activeDispenseIndex, value: nextQty }));
    setActiveDispenseIndex(null);
  };

  const getActiveCartLabel = () => {
    if (!activeCartItem) return "Medicine Stocks";

    const dosage = getMedicineDosage(activeCartItem);
    return `${activeCartItem?.description ? `${activeCartItem.description} - ` : ""}${
      dosage
        ? `${activeCartItem.abbreviation} (${dosage})`
        : activeCartItem.abbreviation
    }`;
  };

  return (
    <MDBCol md="5" className="mb-5">
      {selectedAppointment ? (
        <div className="border rounded p-3 h-100 bg-white">
          <div className="font-weight-bold text-uppercase small text-muted mb-3">
            Menu
          </div>

          <div className="mb-3">
            <SearchClinicMenus
              setMenu={handleMenuSelect}
              clinic={selectedAppointment?.clinicId}
              physicianId={
                selectedAppointment?.physicianId ||
                selectedAppointment?.physician?._id
              }
            />
          </div>

          <table className="menus-clinic-table">
            <thead>
              <tr>
                <th className="text-left">Menus</th>
                <th style={{ width: "75px" }}>UP</th>
                <th style={{ width: "90px" }}>Qty</th>
                <th style={{ width: "110px" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {!cart.length && (
                <tr>
                  <td colSpan="4" className="menus-empty">
                    <span>Select clinic menus to start billing.</span>
                  </td>
                </tr>
              )}

              {cart.map((item, index) => {
                const {
                  _id,
                  description,
                  abbreviation,
                  qty = 1,
                  itemType = "",
                } = item;
                const { up: unitPrice, net: lineTotal } = computeCP(
                  item,
                  privilege,
                );
                const isMedicine =
                  String(itemType || "").toLowerCase() === "meds";
                const dosage = getMedicineDosage(item);

                return (
                  <tr key={`${_id}-${index}`}>
                    <td className="text-left">
                      <span>
                        {description ? `${description} - ` : ""}
                        {dosage ? `${abbreviation} (${dosage})` : abbreviation}
                      </span>
                    </td>
                    <td>{currency.format(unitPrice)}</td>
                    <td className="menus-clinic-qty">
                      {isMedicine ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 font-weight-bold"
                          onClick={() => setActiveDispenseIndex(index)}
                          title="Edit dispense"
                        >
                          {qty}
                        </button>
                      ) : (
                        <span className="text-muted">--</span>
                      )}
                    </td>
                    <td>
                      <span className="text menus-up">
                        {currency.format(lineTotal)}
                      </span>
                      <button
                        onClick={() => dispatch(RemoveFromCart(index))}
                        className="menus-clinic-button-delete"
                      >
                        <MDBIcon icon="trash" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border rounded p-3 h-100 bg-white text-center text-muted py-5">
          <MDBIcon icon="cash-register" size="2x" className="mb-3" />
          <div>Select a confirmed appointment from the dropdown.</div>
        </div>
      )}

      <MDBModal
        isOpen={activeDispenseIndex !== null}
        toggle={() => setActiveDispenseIndex(null)}
        size="lg"
      >
        <MDBModalHeader
          toggle={() => setActiveDispenseIndex(null)}
          className="light-blue darken-3 white-text"
        >
          <div className="d-flex flex-column">
            <span style={{ fontSize: "1rem", fontWeight: 600 }}>
              <MDBIcon icon="capsules" className="mr-2" />
              {getActiveCartLabel()}
            </span>
            <span style={{ fontSize: "0.95rem", opacity: 0.9 }}>
              Select the actual stocks to dispense for this medicine.
            </span>
          </div>
        </MDBModalHeader>
        <MDBModalBody>
          {activeStockRows.length ? (
            <>
              <div className="table-responsive">
                <table className="table table-sm table-bordered mb-0">
                  <thead>
                    <tr>
                      <th>Batch</th>
                      <th>Expiry</th>
                      <th className="text-center">Stock</th>
                      <th className="text-center">Dispense</th>
                      <th className="text-center">Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalStockRows.map((row) => (
                      <tr key={`${row.menuId}-${row.inventoryId}`}>
                        <td>{row.batchNo || "N/A"}</td>
                        <td>
                          {row.expiry
                            ? new Date(row.expiry).toLocaleDateString("en-PH")
                            : "N/A"}
                        </td>
                        <td className="text-center">{row.stock}</td>
                        <td className="text-center" style={{ width: "120px" }}>
                          <input
                            type="number"
                            min="0"
                            max={row.stock}
                            value={String(row.dispense || 0)}
                            className="form-control form-control-sm text-center"
                            onChange={({ target }) =>
                              handleDispenseChange(
                                row.inventoryId,
                                target.value.replace(/[^0-9]/g, ""),
                              )
                            }
                          />
                        </td>
                        <td className="text-center">{row.remaining}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="small text-muted">
                  Click the quantity in the menu table anytime to reopen this
                  dispense window.
                </div>
                <MDBBtn
                  size="sm"
                  color="primary"
                  className="m-0"
                  onClick={handleDispenseSave}
                >
                  Done
                </MDBBtn>
              </div>
            </>
          ) : (
            <div className="text-center text-muted py-4">
              No available stocks found for this medicine.
            </div>
          )}
        </MDBModalBody>
      </MDBModal>
    </MDBCol>
  );
}
